"use client";

// ──────────────────────────────────────────────────────────────────────────
//  Equipment fetch — pulls a character's currently-equipped items from
//  /api/characters/{id}/equipment so we can auto-populate the engine's
//  "already at Myth" state.
//
//  Stale-while-revalidate cached in localStorage with a short TTL (5 min):
//  gear changes during play, but we don't want to hammer the backend on
//  every page nav either. The dashboard explicitly invalidates after a
//  character is selected so the next click feels live.
//
//  ── App-level auto-sync ─────────────────────────────────────────────
//  `useEnsureEquipmentLoaded()` is mounted in the app shell so equipment
//  fetches happen on EVERY page (not just /dashboard). Without this,
//  navigating directly to /recommendations or /bis after sign-in left
//  state.equippedMyth empty until you bounced to /dashboard, and the
//  recommendations engine produced wrong P% (no auto-skip of slots
//  the user already had at Myth).
// ──────────────────────────────────────────────────────────────────────────

import { useEffect, useRef } from "react";
import { API_BASE } from "./api";
import { useCharState, applyEquipmentToMyth } from "./state";
import { useLootCache } from "./loot-cache";
import { useAuth } from "./auth";
import type { EquippedItem } from "./myth";

export interface EquipmentResponse {
  characterID: number;
  items: EquippedItem[];
}

// v2: bumped after adding nameDescription / quality to the response so
// pre-upgrade cached payloads (which lack those fields) get thrown out
// automatically instead of silently classifying every slot as Veteran.
const CACHE_KEY_PREFIX = "bonusroll:equipment:v2:";
const FRESH_MS = 5 * 60 * 1000; // 5 minutes

interface PersistedShape {
  data: EquipmentResponse;
  fetchedAt: number;
}

function readCache(characterID: number): EquipmentResponse | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY_PREFIX + characterID);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedShape;
    if (!p?.data?.items || !p.fetchedAt) return null;
    if (Date.now() - p.fetchedAt > FRESH_MS) return null;
    return p.data;
  } catch {
    return null;
  }
}

function writeCache(characterID: number, data: EquipmentResponse) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY_PREFIX + characterID,
      JSON.stringify({ data, fetchedAt: Date.now() } satisfies PersistedShape),
    );
  } catch { /* quota / private mode — silently ignore */ }
}

/** Imperative fetch — returns null on auth/404/network error. The
 *  dashboard calls this when the user picks a character; no React hook
 *  is needed because we don't render the equipment payload directly. */
export async function fetchEquipment(
  characterID: number,
): Promise<EquipmentResponse | null> {
  const cached = readCache(characterID);
  if (cached) return cached;

  try {
    const resp = await fetch(`${API_BASE}/api/characters/${characterID}/equipment`, {
      credentials: "include",
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as EquipmentResponse;
    writeCache(characterID, data);
    return data;
  } catch {
    return null;
  }
}

/** Power-user helper: clear the cached equipment for one character so
 *  the next call re-fetches. */
export function invalidateEquipment(characterID: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CACHE_KEY_PREFIX + characterID);
  } catch { /* ignore */ }
}

// ── App-level auto-sync hook ────────────────────────────────────────

/** Fires `fetchEquipment` + `applyEquipmentToMyth` whenever the
 *  selected character changes, regardless of which page the user is on.
 *
 *  Mounted by `<EquipmentBootstrap />` in the (app) layout so /pools,
 *  /recommendations, /bis, /admin/upload all benefit. Without this,
 *  recommendations fired with empty `equippedMyth` until the user hit
 *  the dashboard.
 *
 *  Idempotent: a per-character "in flight" guard prevents duplicate
 *  requests when multiple pages mount simultaneously, and the localStorage
 *  cache means we usually skip the network entirely. */
export function useEnsureEquipmentLoaded(): void {
  const state = useCharState();
  const cache = useLootCache();
  const auth = useAuth();
  const lastSyncedID = useRef<number | null>(null);

  useEffect(() => {
    const cid = state.selectedCharacterID;
    if (!cid) return;
    // Don't fetch equipment for anonymous sessions. selectedCharacterID
    // can persist in localStorage across sign-outs, so without this
    // guard every page load post-logout fires GET /equipment with no
    // cookie and gets a 401 in the console for no reason.
    if (auth.state !== "signed-in") return;
    // Already kicked off a fetch for this character — don't re-fire on
    // every render. The localStorage cache handles same-character revisits.
    if (lastSyncedID.current === cid) return;
    lastSyncedID.current = cid;

    let cancelled = false;
    void fetchEquipment(cid).then((eq) => {
      if (cancelled) return;
      if (eq) applyEquipmentToMyth(eq.items, cache);
    });
    return () => { cancelled = true; };
  }, [state.selectedCharacterID, cache, auth.state]);
}
